import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/pages/home/components/ArticleCard";
import { articles, categories } from "@/mocks/mangaData";
import { formatViews } from "@/utils/format";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);
  const category = categories.find((c) => c.id === article?.categoryId);

  const related = useMemo(() => {
    if (!article) return [];
    return articles.filter((a) => a.categoryId === article.categoryId && a.id !== article.id).slice(0, 3);
  }, [article]);

  const hotArticles = useMemo(
    () => [...articles].sort((a, b) => b.views - a.views).slice(0, 5),
    [],
  );

  const jsonLd = useMemo(() => {
    if (!article) return [];
    const base = "https://animesync.github.io";
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        image: article.cover,
        datePublished: article.date,
        dateModified: article.date,
        author: { "@type": "Person", name: article.author },
        publisher: { "@type": "Organization", name: "日漫情报站" },
        mainEntityOfPage: { "@type": "WebPage", "@id": `${base}/article/${article.id}` },
        articleSection: category?.name,
        keywords: article.tags.join(","),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: base },
          { "@type": "ListItem", position: 2, name: category?.name ?? "分类", item: `${base}/category/${article.categoryId}` },
          { "@type": "ListItem", position: 3, name: article.title, item: `${base}/article/${article.id}` },
        ],
      },
    ];
  }, [article, category]);

  return (
    <>
      {article ? (
        <Seo
          title={`${article.title} | 日漫情报站`}
          description={article.excerpt}
          keywords={article.tags.join(",")}
          path={`/article/${article.id}`}
          type="article"
          image={article.cover}
          jsonLd={jsonLd}
        />
      ) : (
        <Seo
          title="文章未找到 - 日漫情报站"
          description="您访问的文章不存在或已被调整，请返回首页浏览其他日漫资讯。"
          path="/article"
        />
      )}

      {!article ? (
        <section className="mx-auto max-w-7xl px-4 py-20 text-center md:px-6">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="mt-6 font-heading text-3xl text-foreground-950">文章不存在或已调整</h1>
          <p className="mt-3 text-sm text-foreground-600">该文章可能已被移除，你可以返回首页继续浏览其他日漫资讯。</p>
          <Link
            to="/"
            className="mt-6 inline-flex whitespace-nowrap items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-5-line" /> 返回首页
          </Link>
        </section>
      ) : (
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: category?.name ?? "分类", path: `/category/${article.categoryId}` },
              { label: article.title },
            ]}
          />

          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
            <article className="lg:col-span-2">
              <header>
                <div className="flex flex-wrap items-center gap-2">
                  {category && (
                    <Link
                      to={`/category/${category.id}`}
                      className="inline-flex whitespace-nowrap items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
                    >
                      <i className={category.icon} />
                      {category.name}
                    </Link>
                  )}
                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                    <i className="ri-eye-line" />
                    {formatViews(article.views)} 阅读
                  </span>
                </div>
                <h1 className="mt-4 text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">{article.title}</h1>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
                <div className="mt-5 flex flex-wrap items-center gap-4 border-y border-background-200 py-4">
                  <span className="flex items-center gap-2 text-sm text-foreground-700">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-100 text-accent-800">
                      <i className="ri-user-line" />
                    </span>
                    <span className="font-medium">{article.author}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-sm text-foreground-500">
                    <i className="ri-calendar-line" /> {article.date}
                  </span>
                  {article.updateInfo && (
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600">
                      <i className="ri-time-line" /> {article.updateInfo}
                    </span>
                  )}
                </div>
              </header>

              <figure className="mt-6 overflow-hidden rounded-lg">
                <img
                  src={article.cover}
                  alt={article.title}
                  className="h-full w-full object-cover object-center"
                />
                <figcaption className="bg-background-100 px-4 py-2 text-xs text-foreground-500">
                  {article.title} · 封面示意图
                </figcaption>
              </figure>

              <div className="mt-6 space-y-4">
                {article.content.map((paragraph, index) => (
                  <p key={index} className="text-base leading-8 text-foreground-800">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                    #{tag}
                  </span>
                ))}
              </div>
            </article>

            <aside className="space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-lg text-foreground-950">热门文章</h2>
                <div className="mt-4 space-y-3">
                  {hotArticles.map((a, index) => (
                    <Link key={a.id} to={`/article/${a.id}`} className="group flex items-start gap-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary-100 font-heading text-sm text-secondary-900">
                        {index + 1}
                      </span>
                      <span className="line-clamp-2 text-sm leading-snug text-foreground-700 transition-colors group-hover:text-primary-700">
                        {a.title}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-lg text-foreground-950">追更提醒</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  想要第一时间收到最新话与连载更新提醒？订阅我们的资讯快报吧。
                </p>
                <Link
                  to="/about#contact"
                  className="mt-4 inline-flex whitespace-nowrap items-center gap-2 rounded-full bg-secondary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-secondary-600"
                >
                  <i className="ri-mail-send-line" /> 订阅资讯
                </Link>
              </div>
            </aside>
          </div>

          {related.length > 0 && (
            <section className="mt-12" aria-labelledby="related-heading">
              <div className="flex items-center justify-between">
                <h2 id="related-heading" className="font-heading text-2xl text-foreground-950">
                  相关推荐
                </h2>
                {category && (
                  <Link
                    to={`/category/${category.id}`}
                    className="inline-flex whitespace-nowrap items-center gap-1 text-sm text-primary-600 transition-colors hover:text-primary-700"
                  >
                    更多{category.name} <i className="ri-arrow-right-line" />
                  </Link>
                )}
              </div>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </>
  );
}